import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { noop, Subject } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  BASE_CUSTOMER_OFFER_STATUS_OPTIONS,
  BASE_PERMISSIONS,
  BASE_STICKY_HEADER_SCROLL_DISTANCE,
  BaseButtonSize,
  BaseCoreState,
  BaseCustomerInquiryOfferInterface,
  BaseFilterService,
  baseInquiryActions,
  BaseInquiryDate,
  BaseInquiryDateService,
  BaseInquiryOfferOrderDirection,
  BaseInquiryOfferOrderKey,
  BaseInquiryOffersFilters,
  baseInquirySelectors,
  BaseInquiryService,
  BaseInquiryShiftService,
  BaseInquiryStatus,
  BaseModalService,
  BaseOrderDirectionEnum,
  BaseQueryParamService,
  BaseRolesService,
  InquiryOffersPagination,
  InquiryResponse,
  MetaPagination,
  OffersCountResponse,
  PspBranchFilterInfoResponse,
  QualificationShortInfoResponse,
  ShiftTimeResponse,
  ShiftTimeAndFlexResponse,
  BaseCustomerInquiryFilters,
  BaseHelpersService,
  InquiryDateResponse,
} from 'base';
import { BaseCustomerInquiryOffersSortKeysInterface } from '../../_interfaces/base-customer-inquiry-offers-sort-keys.interface';
import { BaseCustomerInquiryExperienceOptionsConst } from '../../_const/base-customer-inquiry-offers-filters-options.const';
import { BASE_CUSTOMER_WORK_COUNCIL_APPROVAL_OPTIONS } from '../../../offers/_enums/base-customer-offers.enum';
import { BaseCustomerInquiryService } from '../../inquiry.service';

@Component({
  selector: 'base-customer-inquiry-offers',
  templateUrl: 'base-customer-inquiry-offers.component.html',
})
export class BaseCustomerInquiryOffersComponent implements OnInit, OnDestroy {
  @ViewChild('closeInquiryModal', { static: true }) closeInquiryModal: ElementRef;
  @ViewChild('staffRequest', { static: true }) staffRequest: ElementRef;

  offers: BaseCustomerInquiryOfferInterface[] = [];
  filteredOffers: BaseCustomerInquiryOfferInterface[] = [];
  offersCount: OffersCountResponse;
  mainQualifications: QualificationShortInfoResponse[];
  branches: PspBranchFilterInfoResponse[];
  inquiry: InquiryResponse;
  copiedInquiry: InquiryResponse;
  shiftTimes: (ShiftTimeResponse & { index: number; label?: string })[] = [];
  shiftTimesWithFlex: (ShiftTimeAndFlexResponse & { index: number; label?: string })[] = [];
  currentDate = new Date();
  listOrderKey: BaseInquiryOfferOrderKey | undefined;
  listOrderDirection: BaseInquiryOfferOrderDirection | undefined;
  canPublishInquiry: boolean;
  datesArray: BaseInquiryDate[] = [];
  inquiryListActiveFilters: { [key in any]: any } = {};
  isSidebarVisible = false;
  hasFiltersValues = false;
  isSticky = false;
  stickyHeaderScrollDistance = BASE_STICKY_HEADER_SCROLL_DISTANCE;
  pagination: MetaPagination;
  modalRef: NgbModalRef;
  onInquiryCloseModalRef: NgbModalRef;
  baseInquiryStatus = BaseInquiryStatus;
  buttonSizes = BaseButtonSize;
  inquiryStatus = BaseInquiryStatus;
  baseInquiryExperienceOption = BaseCustomerInquiryExperienceOptionsConst;
  baseOfferStatusOption = BASE_CUSTOMER_OFFER_STATUS_OPTIONS;
  baseCustomerWCApprovalOptions = BASE_CUSTOMER_WORK_COUNCIL_APPROVAL_OPTIONS;
  filtersForm = this.fb.group({
    [BaseInquiryOffersFilters.status]: undefined,
    [BaseInquiryOffersFilters.workCouncilStatus]: undefined,
    [BaseInquiryOffersFilters.rating]: 0,
    [BaseInquiryOffersFilters.experience]: undefined,
    [BaseInquiryOffersFilters.pspBranches]: [[] as number[]],
    [BaseInquiryOffersFilters.mainQualifications]: [[] as number[]],
    [BaseCustomerInquiryFilters.search]: [''],
  });

  private destroy$ = new Subject();

  constructor(
    private fb: NonNullableFormBuilder,
    private rolesService: BaseRolesService,
    private queryParamsService: BaseQueryParamService,
    public filterService: BaseFilterService,
    private modalService: BaseModalService,
    private baseInquiryService: BaseInquiryService,
    private router: Router,
    private store: Store<BaseCoreState>,
    private baseInquiryDateService: BaseInquiryDateService,
    private baseInquiryShiftService: BaseInquiryShiftService,
    private activatedRoute: ActivatedRoute,
    private baseCustomerInquiryService: BaseCustomerInquiryService
  ) {}

  ngOnInit() {
    [this.listOrderKey, this.listOrderDirection] = this.filterService.setOrderParametersByQueryParameters();

    this.getInquiryActiveFilters();
    this.fetchFilteredOffers();
    this.selectCustomerInquiry();
    this.selectInquiryOffers();
    this.toggleSidebarVisibility();
    this.initFilterValuesByQueryParams();
    this.handleClosePageStatus();

    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  sortOffersList(item: any): void {
    if (this.offers.length) {
      this.setOrderDirection(item);
      this.setQueryParams(item.orderKey, this.listOrderDirection);
      this.loadInquiryOffers();
    }
  }

  openNewStaffRequestModal(): void {
    this.modalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.modalRef.result.then(() => {
      this.isSidebarVisible = false;
    }, noop);
  }

  handleClick($event: string) {
    if ($event.includes('action.close')) {
      this.onClose();
    }
    if ($event.includes('action.publish')) {
      this.onPublish();
    }
  }

  onStickyChange(isSticky: boolean): void {
    this.isSticky = isSticky;
  }

  loadOffersNextPage(): void {
    if (this.pagination && this.pagination.page.current < this.pagination.page.total) {
      this.store.dispatch(
        baseInquiryActions.loadCustomerInquiryOffersList({
          payload: {
            ...this.activatedRoute.snapshot.queryParams,
            experience: BaseHelpersService.stringToBoolean(this.activatedRoute.snapshot.queryParams.experience),
            inquiry: this.inquiry.id as number,
            loadNextPage: true,
            page: this.pagination.page.current + InquiryOffersPagination.page,
          },
        })
      );
    }
  }

  copyInquiryHandler(event: Event, inquiry: InquiryResponse) {
    event.stopPropagation();
    this.copyInquiry(inquiry.id as number);
  }

  private copyInquiry(inquiryId: number) {
    this.baseCustomerInquiryService.getInquiry(inquiryId).subscribe((inquiry) => {
      this.copiedInquiry = { ...inquiry, id: undefined };

      const isMixedShifts = this.baseCustomerInquiryService.defineIsShiftTypesMixed(this.copiedInquiry);

      if (isMixedShifts) {
        this.baseCustomerInquiryService.openShiftViewPage(this.copiedInquiry);
      } else {
        this.openNewStaffRequestModal();
      }
    });
  }

  private toggleSidebarVisibility(): void {
    this.baseInquiryService.toggleInquirySidebarSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => (this.isSidebarVisible = visible));
  }

  private onPublish(): void {
    const modalRef = this.modalService.open(
      {
        headText: 'inquiry.publishInquiryNow',
        bodyText: 'inquiry.publishInquiryConfirmText',
        okText: 'action.yesPublish',
        cancelButtonBackground: 'secondary',
        cancelButtonDisplayed: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'md',
      }
    );

    modalRef.result.then(() => {
      this.updateCustomerInquiryStatus(BaseInquiryStatus.published);
    }, noop);
  }

  private selectInquiryOffers() {
    this.store
      .select(baseInquirySelectors.getCustomerInquiryOffers)
      .pipe(
        takeUntil(this.destroy$),
        filter((response) => !!response.ok)
      )
      .subscribe((offersState) => {
        this.mainQualifications = offersState.filters?.mainQualifications as QualificationShortInfoResponse[];
        this.branches = offersState.filters?.branches as PspBranchFilterInfoResponse[];
        this.offers = offersState.data;
        this.filteredOffers = this.offers;
        this.pagination = offersState.pagination as MetaPagination;
      });
  }

  private selectCustomerInquiry() {
    this.store
      .select(baseInquirySelectors.getCustomerInquiry)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((response) => response.data as InquiryResponse)
      )
      .subscribe((inquiry) => {
        const inquiryDates = inquiry.dates as InquiryDateResponse[];

        this.shiftTimes = [];
        this.inquiry = inquiry as InquiryResponse;
        this.offersCount = inquiry.offersCount as OffersCountResponse;
        this.copiedInquiry = { ...this.inquiry };
        const currentDate = inquiryDates[0]?.date ? new Date(inquiryDates[0]?.date) : new Date();
        this.datesArray = this.baseInquiryDateService.prepareDates(inquiry, currentDate);

        if (this.inquiry.shiftTimes?.length) {
          this.shiftTimes = this.baseInquiryShiftService.addIndexAndLabelToShiftTimes(this.inquiry.shiftTimes);
          this.shiftTimes = this.baseInquiryShiftService.sortShiftTimes(this.shiftTimes);
        }
        this.shiftTimesWithFlex = this.baseInquiryShiftService.addFlexShiftToShiftTimes(this.shiftTimes);

        this.baseInquiryService.inquirySubject$.next(this.inquiry);
        this.baseInquiryShiftService.shiftTimesWithFlexSubject$.next(this.shiftTimesWithFlex);
        this.baseInquiryDateService.datesArraySubject$.next(this.datesArray);
      });
  }

  private initFilterValuesByQueryParams(): void {
    const filtersValues = this.filterService.setInitialFilterValuesByQueryParams(
      this.filtersForm.value,
      {
        [BaseInquiryOffersFilters.mainQualifications]: this.filterService.convertArrayToKeyValueObject(
          this.mainQualifications,
          'id',
          'name'
        ),
        [BaseInquiryOffersFilters.pspBranches]: this.filterService.convertArrayToKeyValueObject(
          this.branches,
          'id',
          'name'
        ),
        [BaseInquiryOffersFilters.rating]: this.queryParamsService.getQueryParam('rating'),
        [BaseInquiryOffersFilters.experience]: this.filterService.convertArrayToKeyValueObject(
          this.baseInquiryExperienceOption,
          'value',
          'label'
        ),
        [BaseInquiryOffersFilters.workCouncilStatus]: this.filterService.convertArrayToKeyValueObject(
          this.baseCustomerWCApprovalOptions,
          'value',
          'name'
        ),
      },
      ['rating', 'search']
    );

    this.hasFiltersValues = this.filterService.hasFilterValues(this.filtersForm.value);

    if (!this.hasFiltersValues) {
      return;
    }
    const statusValue = this.queryParamsService.getQueryParam('status');
    this.filtersForm.patchValue({
      ...filtersValues,
      experience: filtersValues.experience
        ? {
            value: JSON.parse(filtersValues.experience[0].value as string),
            label: filtersValues.experience[0].name,
          }
        : undefined,
      status: filtersValues.status
        ? this.baseOfferStatusOption.find((item) => item.id === parseInt(statusValue))
        : undefined,
    });
  }

  private onClose(): void {
    this.onInquiryCloseModalRef = this.modalService.open(
      {
        headText: 'inquiry.closeInquiry.headText',
        bodyTemplate: this.closeInquiryModal,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'lg',
      }
    );

    this.onInquiryCloseModalRef.result.then((value: string) => {
      this.updateCustomerInquiryStatus(BaseInquiryStatus.closed, value);
    }, noop);
  }

  private updateCustomerInquiryStatus(status: 1 | 2 | 3, closedNote?: string): void {
    this.isSidebarVisible = false;
    this.store.dispatch(
      baseInquiryActions.updateInquiryStatus({
        payload: { inquiry: this.copiedInquiry.id as number, status, closedNote },
      })
    );
    this.router.navigate(['inquiries/list'], {
      queryParams: { status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` },
    });
  }

  private loadInquiryOffers(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    const payload = {
      ...queryParams,
      inquiry: this.copiedInquiry.id as number,
      profileAverageRating: queryParams.rating ? parseInt(queryParams?.rating, 10) : undefined,
      experience: BaseHelpersService.stringToBoolean(this.activatedRoute.snapshot.queryParams.experience),
    };
    this.store.dispatch(
      baseInquiryActions.loadCustomerInquiryOffersList({
        payload: payload,
      })
    );
  }

  private setQueryParams(orderKey?: string, orderDirection?: string): void {
    if (orderKey && orderDirection) {
      this.queryParamsService.addParameter('order', orderKey + ',' + orderDirection);
    } else {
      this.queryParamsService.removeParameter('order');
    }
  }

  private setOrderDirection(sortItem: BaseCustomerInquiryOffersSortKeysInterface): void {
    if (this.listOrderKey === sortItem.orderKey) {
      switch (this.listOrderDirection) {
        case BaseOrderDirectionEnum.asc:
          this.listOrderDirection = BaseOrderDirectionEnum.desc;
          break;
        case BaseOrderDirectionEnum.desc:
          this.listOrderDirection = BaseOrderDirectionEnum.asc;
          break;
        default:
          this.listOrderDirection = undefined;
          break;
      }
    } else {
      this.listOrderKey = sortItem.orderKey ? sortItem.orderKey : undefined;
      this.listOrderDirection = sortItem.orderDirection ? sortItem.orderDirection : undefined;
    }
  }

  private fetchFilteredOffers() {
    this.queryParamsService.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
      this.queryParamsService.redirectWithQueryParams(
        {
          ...value,
          filters: {
            ...value?.filters,
            experience: value?.filters?.experience?.value?.toString(),
            status: value?.filters?.status?.id?.toString(),
            workCouncilStatus: value?.filters?.workCouncilStatus?.value?.toString(),
          },
        },
        { search: 'value' },
        () => this.loadInquiryOffers()
      );
    });
  }

  private getInquiryActiveFilters(): void {
    this.store
      .select(baseInquirySelectors.getCustomerSelectedFilters)
      .pipe(
        takeUntil(this.destroy$),
        map((state) => state.data)
      )
      .subscribe((data) => {
        if (data) {
          this.inquiryListActiveFilters = { ...data.filters };

          for (let i in this.inquiryListActiveFilters) {
            this.inquiryListActiveFilters[i] = this.inquiryListActiveFilters[i].toString();
          }
        }
      });
  }

  private handleClosePageStatus(): void {
    this.store
      .select(baseInquirySelectors.getInquiryOffersCloseStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isClosed) => {
        if (isClosed) {
          this.router.navigate(['inquiries/list'], {
            queryParams:
              Object.entries(this.inquiryListActiveFilters).length === 0
                ? { status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` }
                : this.inquiryListActiveFilters,
          });
        }
      });
  }
}
